import React from "react";
import { Message } from "@/lib/types";

type Sender = "user" | "assistant";
type Options = { storageKey: string; keepAlive?: boolean };

// local helper
function cryptoRandomId() {
  try { return crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2); }
}

export function useChatWithPersistence(opts?: Options) {
  const inert = {
    messages: [] as Array<Message>,
    isSending: false,
    isTyping: false,
    send: async (_t: string, _meta?: any) => {},
    appendAssistant: (_t: string) => {},
    appendLocal: (_t: string, _sender?: Sender) => {},
    clear: () => {},
  };
  if (!opts?.storageKey) return inert;

  const { storageKey, keepAlive } = opts;

  // 1) Hydrate once
  const [messages, setMessages] = React.useState<Array<Message>>(() => {
    try { 
      console.log("[chat] storageKey", storageKey);
      return JSON.parse(localStorage.getItem(storageKey) || "[]"); 
    } catch { 
      return []; 
    }
  });
  const [isSending, setIsSending] = React.useState(false);
  const [isTyping, setIsTyping] = React.useState(false);

  // 2) Persist on change
  React.useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(messages)); } catch {}
  }, [storageKey, messages]);

  // 3) DO NOT clear on unmount unless explicitly asked
  React.useEffect(() => {
    if (keepAlive) return () => {};
    return () => {}; // no-op
  }, [keepAlive]);

  const appendLocal = (text: string, sender: Sender = "user") => {
    const m: Message = { id: cryptoRandomId(), sender, content: text, createdAt: new Date().toISOString() };
    setMessages((arr) => [...arr, m]);
  };

  const appendAssistant = (text: string) => appendLocal(text, "assistant");

  const send = async (text: string, meta?: { endpoint: string; body: any }) => {
    if (!text?.trim()) return;
    console.log("[chat] send", { storageKey, text });
    appendLocal(text, "user"); // optimistic echo
    setIsSending(true);
    try {
      setIsTyping(true);
      const { api } = await import("@/lib/api/client");
      const res = await api.post(meta?.endpoint || "/api/colony/_/interact", meta?.body || {});
      const reply = res?.text || res?.envelope?.narration;
      console.log("[chat] reply", (reply || "").slice(0, 80));
      if (reply) appendAssistant(reply);
    } catch (e: any) {
      console.error("[chat] send error", {
        storageKey,
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
      });
      appendAssistant("⚠️ Connection hiccup. I saved your message—try again in a moment.");
    } finally {
      setIsTyping(false);
      setIsSending(false);
    }
  };

  const clear = () => setMessages([]);

  return { messages, isSending, isTyping, send, appendAssistant, appendLocal, clear };
}